import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { t } from 'i18next'

const OrderCorrectionProductModal = ({
  show,
  onClose,
  onResetPagination,
  productList,
  onToggle,
  onPageChange,
  pagination,
  items,
  handleSubmitSearch,
  setKeyword,
  keyword,
  handleReset,
}) => {
  const modalRef = useRef()

  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose?.()
      onResetPagination?.()
    }
  }
  const isAlreadyAdded = (uid) => {
    return items.some((dat) => dat.product_uid === uid)
  }

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose?.()
        onResetPagination?.()
      }
    }

    document.addEventListener('keydown', handleEsc)

    if (show) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = 'auto'
    }
  }, [show, onClose, onResetPagination])

  if (!show) return null

  const currentPage = pagination?.page || 1
  const totalPages = pagination?.total_pages || 1

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        inset: 0,
        overflowY: 'auto',
        zIndex: 1050,
      }}
      onClick={handleClickOutside}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl w-100" role="document">
        <div
          className="modal-content"
          ref={modalRef}
          style={{ maxWidth: '1000px', margin: 'auto' }}
        >
          <div className="modal-header d-flex align-items-center ">
            <div className="d-flex justify-content-between align-items-center w-100">
              <h5 className="modal-title fw-bold">{t('title.product.product_selection')}</h5>
              <form className="js-focus-state" onSubmit={handleSubmitSearch}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control py-2 pl-3 font-size-15 border-right-0 height-40 border-width-2 rounded-left-pill border-primary"
                    name="text"
                    id="searchproduct-item"
                    placeholder={t('placeholder.search_keyword')}
                    aria-label={t('placeholder.search_keyword')}
                    aria-describedby="searchProduct1"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                  <div className="input-group-append">
                    <button
                      className={`btn height-40 text-gray-60 font-size-22 font-weight-bolder border-top border-bottom border-left-0 border-right-0 rounded-0 border-primary border-width-2 pl-2 pr-4 py-2 ${
                        keyword === '' ? 'd-none' : 'd-block'
                      }`}
                      aria-label="Close"
                      type="button"
                      onClick={handleReset}
                    >
                      <span aria-hidden="true" className="position-absolute-center">
                        &times;
                      </span>
                    </button>

                    <button
                      className="btn btn-primary height-40 py-2 px-3 rounded-right-pill"
                      id="searchProduct1"
                      type="submit"
                    >
                      <span className="ec ec-search font-size-24" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <button
              type="button"
              className="btn btn-danger ml-3 position-relative"
              onClick={() => {
                onClose?.()
                onResetPagination?.()
              }}
            >
              <i className="fa fa-times"></i>
            </button>
          </div>

          <div
            className="modal-body"
            style={{
              maxHeight: '60vh',
              overflowY: 'scroll',
              position: 'relative',
              willChange: 'transform',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {productList?.length === 0 && (
              <div className="text-center text-muted">{t('title.product.product_not_found')}</div>
            )}

            {productList?.map((item, index) => (
              <div
                key={index}
                className={`d-flex align-items-center border rounded p-2 mb-3 ${
                  isAlreadyAdded(item?.product_uid) ? 'border-primary' : ''
                }`}
                style={{ minHeight: '70px' }}
              >
                <img
                  src={item.product_thumbnail}
                  alt={item.product_title}
                  width="60"
                  height="60"
                  className="rounded mr-3"
                  style={{ objectFit: 'cover' }}
                  loading="lazy"
                  decoding="async"
                />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div
                    className="font-weight-bold text-truncate product-title-ellipsis"
                    style={{ maxWidth: '70%' }}
                    title={item.product_title}
                  >
                    {item.product_title}
                  </div>
                  <div>Rp {item.product_price?.toLocaleString?.('id-ID') || 0}</div>
                </div>
                <div>
                  <button
                    className={`btn btn-sm pointer ${
                      isAlreadyAdded(item?.product_uid) ? 'btn-danger' : 'btn-primary'
                    } ms-3`}
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => onToggle?.(item)}
                  >
                    {isAlreadyAdded(item?.product_uid)
                      ? t('button.delete')
                      : `+ ${t('button.add')}`}
                  </button>
                  {isAlreadyAdded(item?.product_uid) && (
                    <label className="ml-3 mr-2">
                      <b>{t('title.product.product_selected')}</b>
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="modal-footer d-flex justify-content-between">
            <button
              className="btn btn-outline-primary btn-sm pointer"
              disabled={currentPage <= 1}
              onClick={() => onPageChange?.(currentPage - 1)}
            >
              ← {t('title.previous')}
            </button>
            <span className="text-muted small align-self-center">
              {t('title.page')} {currentPage} {t('title.from')} {totalPages}
            </span>
            <button
              className="btn btn-outline-primary btn-sm pointer"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange?.(currentPage + 1)}
            >
              {t('title.next')} →
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

OrderCorrectionProductModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onResetPagination: PropTypes.func,
  productList: PropTypes.array,
  onToggle: PropTypes.func,
  onPageChange: PropTypes.func,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    total_pages: PropTypes.number,
    total_items: PropTypes.number,
    limit: PropTypes.number,
  }),
  items: PropTypes.array,
  handleSubmitSearch: PropTypes.func,
  setKeyword: PropTypes.func,
  keyword: PropTypes.string,
  handleReset: PropTypes.func,
}

export default OrderCorrectionProductModal
